import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  DocumentType,
  Document,
  DocumentKkb,
} from "../requestAppSlise/requestAppSlise";
import { validateObjectMember } from "../../../utils/utilities/validateDataToBank";
import { ApplicationBankStatus } from "../../../utils/guards/ApplicationBankStatusGuard/types";

export interface IAccountingBalance {
  id: number;
  active_balance_date: string | null;
  passive_balance_date: string | null;
  financial_result_balance_date: string | null;
  application_id: number;
}

export interface IBankRequisites {
  id: number;
  application_id: number;
  bik: string | null;
  name_bank: string | null;
  account_number: string | null;
  account_correspondent: string | null;
}

export interface IContactApplication {
  id: number;
  application_id: number;
  actual_address_organization: boolean;
  post_address_organization: string | null;
  address_legal: string;
  address_factual?: string;
  phone: string;
  email: string;
}

export interface DocumentToBank
  extends Omit<DocumentType<Document>, "document"> {
  type: string;
  document?: DocumentKkb | null;
  member_id?: number;
  member_passport_fl_id?: number;
}

export interface ITypeDocumentInfo {
  documents: DocumentToBank[];
  title: string;
}

export interface OfferDocumentType {
  id: number;
  name: string;
  extention: string[];
  type: string;
  document: DocumentKkb;
}

export interface IPassportData {
  member_fl_id: number;
  passport_name: string;
  passport_surname: string;
  passport_patronymic: string;
  passport_series: string;
  passport_number: string;
  passport_division_code: string;
  passport_address: string;
  passport_post_index: string;
  passport_issue_date: string;
  passport_birth: string;
  passport_place_birth: string;
  passport_document?: DocumentToBank;
  passport_department: string;
  passport_post_address: string | null;
}

export interface IMemberFL {
  id: number;
  application_id: number;
  name: string;
  nationality: string | null;
  inn: string;
  inn_org: string | null;
  snils: string | null;
  phone: string | null;
  email: string | null;
  date_birth: string | null;
  is_eio: boolean;
  position?: string;
  acts_pursuant?: string;
  is_founder: boolean;
  percent: string | null;
  member_passport_fl: IPassportData;
  post_and_registration_address: boolean;
}

export interface IMemberUL {
  address_parent_org: string | null;
  application_id: number;
  date_first_registration: string | null;
  date_registration: string;
  employee_number: string;
  id: number;
  inn: string;
  kpp: string;
  link: string;
  name: string;
  ogrn: string;
  okopf: string;
  okfs: string;
  opf_name: string;
  percent: number;
  registration_number: number | null;
  registration_place: string;
  shortname: string;

  address_legal?: string;
  okpo?: string;
}

export interface FinanceRow {
  title: string;
  code: string;
  key: string;
  lc: number;
  rc: number;
}

interface BalanceApplicationType {
  osn: boolean;
  usn_income: boolean;
  usn_income_expenses: boolean;
  envd: boolean;
  patent: boolean;
  simplified_form: boolean;
  full_form_balance_opiu: boolean;
  accountingBalance: {
    balance_date_lc: string | null;
    balance_date_rc: string | null;
    data: {
      active: FinanceRow[];
      passive: FinanceRow[];
      report: FinanceRow[];
    };
  };
}

export interface IApplicationStatus {
  color: "blue" | "purple" | "green" | "red" | "default" | "gold" | "orange";
  created_at: string | null;
  deleted_at: string | null;
  id: number;
  info:
    | "Есть незаполненные поля"
    | "Заявка успешно отправлена в банк"
    | "Заявка одобрена"
    | "Отказано"
    | "Гарантия оплачена"
    | "Черновик"
    | "На доработку"
    | "Предложение"
    | "Отказано клиентом"
    | "Требуется подписание";
  info_color: "info" | "success" | "error";
  title:
    | "Новая заявка"
    | "В работе"
    | "Выпущена"
    | "Отказано банком"
    | "Одобрено"
    | "Черновик"
    | "Заявка ждет изменений"
    | "Предложение от банка"
    | "Отказано"
    | "Требуется подписание";
  type:
    | "new_application"
    | "in_work"
    | "issued"
    | "rejected"
    | "approved"
    | "draft"
    | "to_rework"
    | "status_offer"
    | "rejected_agent"
    | "required_signature";
  updated_at: string;
}

export interface AdditionalDocument
  extends Omit<DocumentType<Document>, "document"> {
  type: string;
  document?: DocumentKkb[] | null;
  member_id?: number;
  member_passport_fl_id?: number;
}
export interface IApplicationDataToBank {
  id: number;
  agent_id: number;
  agent_bank_id: number;
  organization_id: number;
  status_id: number;
  bg_interface_id: number;
  inn: string;
  total_sum: string;
  is_tax_simple: boolean;
  link: string;
  start_bg_at: string;
  finish_bg_at: string;
  is_to_push_bank: boolean;
  accounting_balance: IAccountingBalance[];
  bank_requisites: IBankRequisites;
  contact_application: IContactApplication;
  members_fl: IMemberFL[];
  members_ul: IMemberUL[];
  balance_application: BalanceApplicationType;
  documents: {
    financeDocument: { documents: DocumentToBank[]; title: string };
    mainDocument: { documents: DocumentToBank[]; title: string };
    pasportDocument: { documents: DocumentToBank[]; title: string };
    banksOfferDocuments?: { documents: DocumentToBank[]; title: string };
    additionalDocument?: { documents: AdditionalDocument[]; title: string };
  };
  status: ApplicationBankStatus;
  overstatement: string;
  base_commission: string;
  final_commission: string;
  refusal_info: { text: string } | null;
  bank_rate: string;
  offer: OfferDocumentType[];
}

interface IApplicationDataToBankInitial {
  APPLICATION_DATA_TO_BANK: IApplicationDataToBank | null;
}

const initialState: IApplicationDataToBankInitial = {
  APPLICATION_DATA_TO_BANK: null,
};

const applicationDataToBankSlice = createSlice({
  name: "APPLICATION_DATA_TO_BANK",
  initialState,
  reducers: {
    // записать данные о доп заявке
    SET_APPLICATION_DATA_TO_BANK: (
      state,
      action: PayloadAction<IApplicationDataToBank>
    ) => {
      const membersFL = action.payload?.members_fl?.map((member) => ({
        ...member,
        isRequiredFields: validateObjectMember(member.member_passport_fl, [
          "passport_post_address",
          "passport_url",
          "passport_url_sign",
          "expired_date",
          "passport_document",
        ]),
      }));

      state.APPLICATION_DATA_TO_BANK = {
        ...action.payload,
        members_fl: membersFL,
      };
    },
    // очистить данные о доп заявке
    CLEAR_APPLICATION_DATA_TO_BANK: (state) => {
      state.APPLICATION_DATA_TO_BANK = null;
    },
  },
});

export const {
  SET_APPLICATION_DATA_TO_BANK,

  CLEAR_APPLICATION_DATA_TO_BANK,
} = applicationDataToBankSlice.actions;

export default applicationDataToBankSlice.reducer;
